/**
 * Money and time formatting, shared by both applications (arch §4.2).
 *
 * These live in one place because the customer app and the host panel show the same
 * money to the same people, and a price built by concatenating a pound sign onto a
 * number will eventually be wrong.
 */

/** Money is carried in pence, never as a float. £8.00 is `800`. */
export type Pence = number;

const gbp = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});

/**
 * Format a price for display. Prices are VAT-inclusive throughout: a bid of £8 costs
 * £8 (spec §18.5).
 *
 * Under a pound it is pence — `99p`, `30p` — the way a price that small is written on a shelf,
 * and the way a customer reads it faster than `£0.99`. Zero stays `£0.00`: it is a total or a
 * shipping line here, and a *free* lot says so in words, which is the application's to choose.
 */
export function formatMoney(amount: Pence): string {
  if (!Number.isInteger(amount)) {
    throw new TypeError(`Money must be whole pence, received ${amount}`);
  }
  if (amount !== 0 && Math.abs(amount) < 100) return `${amount}p`;
  return gbp.format(amount / 100);
}

/**
 * Format an instant in the viewer's own time zone. Everything is stored and reasoned
 * about in UTC and displayed locally (spec §18.5).
 */
export function formatDateTime(
  instant: Date,
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" },
): string {
  return new Intl.DateTimeFormat("en-GB", options).format(instant);
}

/**
 * Money as the API sends it: a decimal string of pounds, VAT-inclusive — `"12.50"`.
 *
 * The wire format is Shopify's and Postgres's rather than this file's preferred pence, and that
 * is deliberate in both directions. `numeric(10,2)` comes back from `pg` as a string because
 * only a string can carry a decimal exactly, and Shopify's `Money` is the same string; parsing
 * either one into a float on the way through would introduce the rounding this whole module
 * exists to avoid.
 *
 * So the conversion happens here, once, at the edge where the value becomes something a person
 * reads — and `formatMoney` above stays the only thing that knows how a price looks.
 */
export function parsePrice(decimal: string): Pence {
  // `Math.round` rather than a truncation: `"12.50"` times a hundred is `1249.9999999999998` in
  // binary floating point, and truncating it would show £12.49.
  const pence = Math.round(Number(decimal) * 100);
  if (!Number.isFinite(pence)) {
    throw new TypeError(`Not a price: ${decimal}`);
  }
  return pence;
}

/** True for a decimal price string of nothing — a free lot (`R-LOT-32`). */
export function isFree(decimal: string): boolean {
  return parsePrice(decimal) === 0;
}

/** A decimal price string, formatted. The two calls above, together, because it is always both. */
export function formatPrice(decimal: string): string {
  return formatMoney(parsePrice(decimal));
}
